import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useLocalizeMessage, ModalWindow, Text, Select, useDialog, useToastNotification, } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { DataTransferTemplateContext } from '../../../contexts/data_transfer_template/data_transfer_template.context';
// Helper function to get available status options based on current status
const getAvailableStatusOptions = (currentStatus, allStatusOptions) => {
    // Filter out the current status first
    const availableOptions = allStatusOptions.filter((option) => option.value !== currentStatus);
    // Apply business logic restrictions
    if (currentStatus === 'PUBLISHED') {
        // Published templates can only change to ARCHIVED
        return availableOptions.filter((option) => option.value === 'ARCHIVED');
    }
    if (currentStatus === 'DRAFT') {
        // Draft templates can change to PUBLISHED or ARCHIVED
        return availableOptions.filter((option) => option.value === 'PUBLISHED' || option.value === 'ARCHIVED');
    }
    if (currentStatus === 'ARCHIVED') {
        // Archived templates status cannot be changed
        return [];
    }
    // Default: return all available options (excluding current status)
    return availableOptions;
};
export const ChangeTransferStatusModal = ({ templateId, currentStatus, onClose }) => {
    const translate = useLocalizeMessage();
    const webClient = useApiClient(ApiClientService.WEB);
    const { getStatusBadge, statusOptions } = useContext(DataTransferTemplateContext);
    const dialog = useDialog();
    const toast = useToastNotification();
    const queryClient = useQueryClient();
    const { entityId } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [newStatus, setNewStatus] = useState(getAvailableStatusOptions(currentStatus, statusOptions)[0]);
    const changeStatus = async () => {
        try {
            setIsLoading(true);
            await webClient.patch(`/api/data_transfer/templates/${templateId}`, {
                status: newStatus.value,
            });
            queryClient.invalidateQueries({
                queryKey: ['entities', entityId, 'transfer_templates'],
            });
            queryClient.invalidateQueries({
                queryKey: ['entities', entityId, 'transfer_templates', templateId],
            });
            toast.addNotification({
                type: 'success',
                title: translate('Status updated successfully'),
            });
            onClose();
        }
        catch {
            toast.addNotification({
                type: 'error',
                title: translate('Error updating status'),
            });
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleSubmit = async () => {
        if (newStatus.value === 'ARCHIVED') {
            const ok = await dialog.open({
                title: translate('Archive template?'),
                body: translate('Are you sure you want to archive this template? It will still be available if it is used in a transfer schedule, but will be hidden from the available templates table, unless you turn the “Is archived” filter on.'),
                actionButtons: [{ isDestructive: true, label: translate('Yes, archive') }, { label: translate('No, cancel') }],
            });
            if (ok) {
                changeStatus();
            }
        }
        else {
            changeStatus();
        }
    };
    const submitButton = {
        label: translate('Change status'),
        onClick: handleSubmit,
        disabled: isLoading,
    };
    const footerSecondaryActionButtons = [
        {
            label: translate('Cancel'),
            onClick: onClose,
            disabled: isLoading,
        },
    ];
    return (_jsx(ModalWindow, { title: translate(`Change status`), isOpen: true, closeWindow: onClose, footerPrimaryActionButton: submitButton, footerSecondaryActionButtons: footerSecondaryActionButtons, children: _jsxs("div", { className: 'space-y-4 p-4', children: [_jsxs("div", { className: 'flex gap-12', children: [_jsx(Text, { children: translate(`Current status`) }), getStatusBadge(currentStatus)] }), _jsx(Select, { label: translate('New status'), options: getAvailableStatusOptions(currentStatus, statusOptions), value: newStatus, onChange: (value) => setNewStatus(value), width: 'full' })] }) }));
};
